import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/AppLayout";
import { ConfidentialityBadge, SensitivityBadge } from "@/components/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import ExportMenu from "@/components/ExportMenu";
import { fileDownloadUrl } from "@/lib/download";
import { Plus, Clock, CornerDownRight, CalendarIcon, Paperclip, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { toast } from "sonner";

const COLUMNS = ["reçu", "qualifié", "en instruction", "décision", "transmis", "clôturé"];
const NEXT = { "reçu": "qualifié", "qualifié": "en instruction", "en instruction": "décision", "décision": "transmis", "transmis": "clôturé" };

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" }); } catch { return "—"; }
}

export default function Dossiers() {
  const { user } = useAuth();
  const [dossiers, setDossiers] = useState([]);
  const [open, setOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [form, setForm] = useState({ objet: "", origine: "", entite: "", en_charge: "PARAFIX", sensibilite: "moyenne", confidentialite: "interne", echeance: null });
  const [attachments, setAttachments] = useState({}); // dossier_id -> [files]
  const [uploadingId, setUploadingId] = useState(null);

  const canCreate = user?.role === "admin" || user?.role === "sg";

  const loadAttachments = useCallback(async (did) => {
    const { data } = await api.get(`/files?dossier_id=${did}`);
    setAttachments((prev) => ({ ...prev, [did]: data }));
  }, []);

  const uploadTo = async (did, file) => {
    if (!file) return;
    setUploadingId(did);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dossier_id", did);
      await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Pièce jointe ajoutée");
      loadAttachments(did);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setUploadingId(null);
    }
  };

  const load = useCallback(() => api.get("/dossiers").then((r) => setDossiers(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { dossiers.forEach((d) => loadAttachments(d.id)); }, [dossiers, loadAttachments]);

  const advance = async (d) => {
    const next = NEXT[d.statut];
    if (!next) return;
    await api.patch(`/dossiers/${d.id}/status`, { statut: next });
    toast.success(`${d.reference} → ${next}`);
    load();
  };

  const create = async () => {
    if (!form.objet || !form.origine) { toast.error("Objet et origine requis"); return; }
    try {
      await api.post("/dossiers", { ...form, echeance: form.echeance ? new Date(form.echeance).toISOString() : null });
      toast.success("Dossier créé");
      setOpen(false);
      setForm({ objet: "", origine: "", entite: "", en_charge: "PARAFIX", sensibilite: "moyenne", confidentialite: "interne", echeance: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  return (
    <>
      <PageHeader
        title="Suivi des dossiers"
        subtitle="Vue kanban · cycle de vie, sensibilité, confidentialité et échéances"
        actions={
          <div className="flex items-center gap-3">
            <ExportMenu basePath="/dossiers/export" filename="dossiers_sg_podc" testid="dossiers-export" />
            {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-sm bg-navy hover:bg-navy-dark" data-testid="new-dossier-button">
                <Plus className="h-4 w-4 mr-1.5" /> Nouvelle saisine
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-sm" data-testid="new-dossier-dialog">
              <DialogHeader><DialogTitle className="font-heading text-navy">Fiche de saisine</DialogTitle>
                <DialogDescription>Renseignez l'objet, le demandeur et les niveaux de sensibilité/confidentialité du dossier.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label>Objet</Label>
                  <Input value={form.objet} onChange={(e) => setForm({ ...form, objet: e.target.value })} className="rounded-sm" data-testid="dossier-objet" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Demandeur / origine</Label>
                    <Input value={form.origine} onChange={(e) => setForm({ ...form, origine: e.target.value })} className="rounded-sm" data-testid="dossier-origine" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Entité liée</Label>
                    <Input value={form.entite} onChange={(e) => setForm({ ...form, entite: e.target.value })} className="rounded-sm" data-testid="dossier-entite" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Sensibilité</Label>
                    <Select value={form.sensibilite} onValueChange={(v) => setForm({ ...form, sensibilite: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="dossier-sensibilite"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="faible">Faible</SelectItem>
                        <SelectItem value="moyenne">Moyenne</SelectItem>
                        <SelectItem value="élevée">Élevée</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Confidentialité</Label>
                    <Select value={form.confidentialite} onValueChange={(v) => setForm({ ...form, confidentialite: v })}>
                      <SelectTrigger className="rounded-sm" data-testid="dossier-confidentialite"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="public">Public</SelectItem>
                        <SelectItem value="interne">Interne</SelectItem>
                        <SelectItem value="confidentiel">Confidentiel</SelectItem>
                        <SelectItem value="très confidentiel">Très confidentiel</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Échéance</Label>
                  <Popover open={dateOpen} onOpenChange={setDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        className="w-full justify-start rounded-sm font-normal"
                        data-testid="dossier-echeance"
                      >
                        <CalendarIcon className="h-4 w-4 mr-2 text-muted-foreground" strokeWidth={1.5} />
                        {form.echeance
                          ? format(form.echeance, "d MMMM yyyy", { locale: fr })
                          : <span className="text-muted-foreground">Choisir une date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-sm" align="start">
                      <Calendar
                        mode="single"
                        locale={fr}
                        selected={form.echeance || undefined}
                        onSelect={(d) => { setForm({ ...form, echeance: d || null }); setDateOpen(false); }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} className="rounded-sm bg-navy hover:bg-navy-dark" data-testid="dossier-submit">Créer le dossier</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
            )}
          </div>
        }
      />
      <div className="p-6 overflow-x-auto scroll-thin">
        <div className="flex gap-4 min-w-max pb-4" data-testid="kanban-board">
          {COLUMNS.map((col) => {
            const items = dossiers.filter((d) => d.statut === col);
            return (
              <div key={col} className="w-72 shrink-0" data-testid={`kanban-col-${col}`}>
                <div className="flex items-center justify-between px-1 mb-2">
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{col}</h3>
                  <span className="text-xs text-muted-foreground font-mono">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map((d) => (
                    <div key={d.id} className="bg-white border border-border rounded-sm p-3 hover:border-navy transition-colors" data-testid={`dossier-card-${d.reference}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-mono text-navy font-semibold">{d.reference}</span>
                        <span className="text-[10px] text-muted-foreground font-mono">{d.en_charge}</span>
                      </div>
                      <p className="text-sm text-slate-800 leading-snug mb-2">{d.objet}</p>
                      <div className="flex flex-wrap gap-1 mb-2">
                        <ConfidentialityBadge level={d.confidentialite} />
                        <SensitivityBadge level={d.sensibilite} />
                      </div>
                      <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(d.echeance)}</span>
                        {NEXT[d.statut] && (
                          <button onClick={() => advance(d)} className="flex items-center gap-1 text-navy hover:underline" data-testid={`advance-${d.reference}`}>
                            <CornerDownRight className="h-3 w-3" /> {NEXT[d.statut]}
                          </button>
                        )}
                      </div>
                      <div className="mt-2 pt-2 border-t border-border">
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                            Pièces jointes ({(attachments[d.id] || []).length})
                          </span>
                          <label className="flex items-center gap-1 text-[11px] text-navy hover:underline cursor-pointer" data-testid={`attach-${d.reference}`}>
                            {uploadingId === d.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Paperclip className="h-3 w-3" />} Joindre
                            <input type="file" className="hidden" onChange={(e) => { uploadTo(d.id, e.target.files[0]); e.target.value = ""; }} />
                          </label>
                        </div>
                        {(attachments[d.id] || []).map((f) => (
                          <a key={f.id} href={fileDownloadUrl(f.id)} target="_blank" rel="noreferrer"
                            className="flex items-center gap-1 text-[11px] text-slate-600 hover:text-navy mt-1 truncate" data-testid={`file-${f.id}`}>
                            <Paperclip className="h-3 w-3 shrink-0" /> <span className="truncate">{f.original_filename}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <p className="text-[11px] text-muted-foreground px-1 py-4 text-center border border-dashed border-border rounded-sm">—</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
